import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Checkbox, Grid, TextField } from '@material-ui/core';
import { OnwardChip } from '@/styles/blocks';
import { useSwappedAttributes } from '@/components/ShipmentForm/hooks';
import { LocationType, ServiceType } from '../../InputFields';
import { useClientUser } from '@/hooks';
import { OnwardCheckbox, TabRow, TabSection } from '@/components/ShipmentForm/blocks';
import { ONWARD_TAGS } from '@/components/Account/ServiceLevels/ServiceLevel';
import { Autocomplete } from '@material-ui/lab';
import zipcode_to_timezone from 'zipcode-to-timezone';
import StartTimeSelect from '@/components/misc/StartTimeSelect';
import { useQuery } from '@apollo/client';
import { QUERY_ADMIN_TAGS } from '@/components/admin/AdminTags/graphql';
import PinLabels from './PinLabels';

const DeliveryType = ({ isDirty, order, hasError, callbacks, options }) => {
    const { service_levels, tags, circles } = useClientUser();
    const { location, location_info, location_type, stair_quantity, stairs, elevators, zip } =
        useSwappedAttributes(order);
    const [debrisOverride, setAppendDebris] = useState(false);

    const { data: tagsData } = useQuery(QUERY_ADMIN_TAGS, { variables: {} });

    const appendDebris = useMemo(() => {
        return debrisOverride || !!order.pallet_debris_removal_notes;
    }, [order, debrisOverride]);

    const tagOptions = useMemo(() => {
        const equipment = {
            ...(order.recommended_equipment || {}),
            ...(order.equipment_overrides || {}),
        };
        const serviceLevel = service_levels.find((sl) => sl.service_level_id === order.service_level_id);

        const tagOptions = Object.values({
            ...Object.entries(ONWARD_TAGS).reduce((acc, [value, label]) => {
                let obj = {};
                switch (value) {
                    case '2_man_team':
                    case '3_man_team':
                    case '4_man_team':
                        obj = equipment;
                        break;
                    case 'no_contact':
                    case 'assembly_required':
                    default:
                        obj = order;
                        break;
                }
                return {
                    ...acc,
                    [value]: {
                        value,
                        label,
                        type: 'onward',
                        selected: !!obj[value],
                        disabled: !!obj[value] && !!serviceLevel?.onward_tags?.includes(value),
                    },
                };
            }, {}),
            ...[...(tags || []), ...(tagsData?.results || [])].reduce((acc, t) => {
                const selected = order?.tags?.find((mapping) => mapping.tag_id === t.tag_id);
                acc[t.tag_id] = {
                    value: t.tag_id,
                    label: t.tag,
                    type: 'custom',
                    selected: selected,
                    disabled: selected && serviceLevel?.tags?.find((mapping) => mapping.tag_id === t.tag_id),
                };
                return acc;
            }, {}),
        });

        return tagOptions.sort((a, b) => a.label.localeCompare(b.label));
    }, [order, service_levels, tags, tagsData]);

    const timezone = useMemo(() => {
        const orderZip = order[zip];
        return zipcode_to_timezone.lookup(orderZip) || Intl.DateTimeFormat().resolvedOptions.timeZone;
    }, [order, zip]);

    const warehouseCloseDate = useMemo(() => {
        if (order.pallet_warehouse_close_time) {
            const date = new Date(order.pallet_warehouse_close_time);
            date.setHours(0, 0, 0, 0);
            return date.toISOString();
        }
        return new Date().toISOString();
    }, [order]);

    let content = <></>;
    if (order.freight_type === 'pallet') {
        content = (
            <>
                <TabRow>
                    <Grid item xs={6}>
                        <StartTimeSelect
                            onChange={(e) => {
                                callbacks.modifyOrder({
                                    pallet_warehouse_close_time: new Date(e.target.value).toISOString(),
                                });
                            }}
                            value={
                                order.pallet_warehouse_close_time
                                    ? new Date(order.pallet_warehouse_close_time).getTime()
                                    : 0
                            }
                            timeZone={timezone}
                            deliveryDate={warehouseCloseDate}
                            interval={30}
                            label="Warehouse Closing Time"
                            InputLabelProps={{
                                shrink: !!order.pallet_warehouse_close_time,
                            }}
                        />
                    </Grid>
                </TabRow>
                <TabRow>
                    {[
                        { attr: 'pallet_forklift_at_pickup', label: 'Forklift at Pickup' },
                        { attr: 'pallet_forklift_at_dropoff', label: 'Forklift at Delivery' },
                        { attr: 'pallet_flatbed_truck', label: 'Flatbed Truck' },
                        { attr: 'pallet_unloading_required', label: 'Unload from Pallet' },
                    ].map(({ attr, label }) => (
                        <Grid item xs={6} lg={3}>
                            <OnwardCheckbox
                                label={label}
                                checked={!!order[attr]}
                                onChange={(_, checked) => callbacks.modifyOrder({ [attr]: checked })}
                            />
                        </Grid>
                    ))}
                </TabRow>

                <TabRow>
                    <Grid item xs={12}>
                        <OnwardCheckbox
                            label="Remove Debris"
                            checked={appendDebris}
                            onChange={(_, checked) => {
                                setAppendDebris(checked);
                                if (!checked) {
                                    callbacks.modifyOrder({ pallet_debris_removal_notes: '' });
                                }
                            }}
                        />
                    </Grid>
                </TabRow>

                {appendDebris ? (
                    <TabRow>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={4}
                                placeholder="Debris Description"
                                value={order.pallet_debris_removal_notes}
                                onChange={(e) => {
                                    callbacks.modifyOrder({ pallet_debris_removal_notes: e.target.value });
                                }}
                            />
                        </Grid>
                    </TabRow>
                ) : null}
            </>
        );
    }

    return (
        <>
            <TabSection>Delivery Type</TabSection>
            <TabRow>
                <Grid item xs={3}>
                    <LocationType
                        value={order[location]}
                        onChange={(e) => {
                            callbacks.makeDirty([location]);
                            callbacks.modifyOrder({ [location]: e.target.value });
                        }}
                        error={hasError[location] && (options.startDirty || isDirty[location])}
                    />
                </Grid>
                <Grid item xs={4}>
                    <ServiceType
                        value={order.service_level_id}
                        locationType={order[location]}
                        dropoffType={order[location_type]}
                        order={order}
                        serviceLevels={service_levels}
                        circles={circles}
                        error={hasError[location_type] && (options.startDirty || isDirty[location_type])}
                        onBlur={() => callbacks.makeDirty([location_type])}
                        onChange={(option) => {
                            const {
                                instructions,
                                service_level_id,
                                location_type: dropoffType,
                                tags: serviceTags,
                                onward_tags,
                            } = option;

                            callbacks.makeDirty([location_type]);
                            callbacks.modifyOrder({
                                [location_type]: dropoffType,
                                service_level_id: service_level_id,
                                service_level_instructions: instructions || '',
                                ...(onward_tags || []).reduce((acc, tag) => {
                                    switch (tag) {
                                        case 'no_contact':
                                        case 'assembly_required':
                                            return { ...acc, [tag]: true };
                                        case '2_man_team':
                                        case '3_man_team':
                                        case '4_man_team':
                                            return {
                                                ...acc,
                                                equipment_overrides: {
                                                    ...(order.equipment_overrides || {}),
                                                    [tag]: true,
                                                },
                                            };
                                        default:
                                            return acc;
                                    }
                                }, {}),
                                tags: (serviceTags || []).map((mapping) => ({
                                    tag_id: mapping.tag_id,
                                    ...(order.order_id ? { order_id: order.order_id } : {}),
                                })),
                                ...(!!circles?.['import-lock-wg-light-assembly']
                                    ? {
                                          itemsByOrderId: order.itemsByOrderId.map((item) => ({
                                              ...item,
                                              assembly_type:
                                                  dropoffType === 'whiteGlove' ? 'minimal_assembly' : 'no_assembly',
                                          })),
                                      }
                                    : {}),
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        type="number"
                        variant="outlined"
                        label="Flights of stairs"
                        fullWidth
                        value={order[stair_quantity] || 0}
                        onChange={(e) => {
                            const next = Math.max(0, e.target.value || 0);
                            callbacks.modifyOrder({
                                [stair_quantity]: next,
                                [stairs]: next > 0,
                            });
                        }}
                        onWheel={(event) => event.target.blur()}
                    />
                </Grid>
                <Grid item xs={3}>
                    <OnwardCheckbox
                        fontWeight={400}
                        label="Elevator available?"
                        checked={!!order[elevators]}
                        onChange={(_, checked) => callbacks.modifyOrder({ [elevators]: checked })}
                    />
                </Grid>
            </TabRow>
            {order.order_type !== 'return' && (
                <TabRow>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={5}
                            placeholder="Special Instructions"
                            value={order.special_instructions}
                            onChange={(e) => {
                                callbacks.modifyOrder({ special_instructions: e.target.value });
                            }}
                        />
                    </Grid>
                </TabRow>
            )}
            <TabRow>
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        fullWidth
                        value={tagOptions.filter((tag) => tag.selected)}
                        options={tagOptions}
                        getOptionSelected={(option, value) => {
                            return option.value === value.value;
                        }}
                        getOptionLabel={(option) => option.label}
                        renderTags={(options, getTagProps) => {
                            return options.map((option, index) => (
                                <OnwardChip
                                    key={option.value}
                                    label={option.label}
                                    {...getTagProps({ index })}
                                    {...(option.disabled ? { onDelete: null } : {})}
                                    css={css`
                                        color: ${option.disabled ? '#8d8d8d' : 'black'};
                                    `}
                                />
                            ));
                        }}
                        renderOption={(option, { selected }) => {
                            return (
                                <li key={option.value}>
                                    <Checkbox color="primary" checked={selected} />
                                    {option.label}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Tags"
                                onKeyDown={(e) => {
                                    if (['Backspace'].includes(e.key)) {
                                        e.stopPropagation();
                                    }
                                }}
                            />
                        )}
                        onChange={(e, value = []) => {
                            const onwardTags = value.filter((tag) => tag.type === 'onward').map((tag) => tag.value);
                            const customTagIds = value.filter((tag) => tag.type === 'custom').map((tag) => tag.value);

                            callbacks.modifyOrder({
                                ...Object.entries(ONWARD_TAGS).reduce((acc, [tag]) => {
                                    switch (tag) {
                                        case '2_man_team':
                                        case '3_man_team':
                                        case '4_man_team':
                                            acc.equipment_overrides = {
                                                ...(acc.equipment_overrides || {}),
                                                [tag]: onwardTags.includes(tag),
                                            };
                                            break;
                                        case 'no_contact':
                                        case 'assembly_required':
                                            acc[tag] = onwardTags.includes(tag);
                                            break;
                                    }
                                    return acc;
                                }, {}),
                                tags: customTagIds.map((tagId) => ({
                                    tag_id: tagId,
                                    ...(order.order_id ? { order_id: order.order_id } : {}),
                                })),
                            });
                        }}
                    />
                </Grid>
            </TabRow>
            <PinLabels order={order} callbacks={callbacks} />
            {content}
        </>
    );
};

export default DeliveryType;
